import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { QR_LIST_CONVERSATION } from "../graphQl/MessengerAPI";
import { useNotifications } from "../../../../hooks/NotificationContext";

export const useConversations = (
  initialPaginationParams,
  establishmentId,
  customerId,
  assignedAgentId,
  status,
  options = {}
) => {
  // `status` et `options` sont additifs (défaut undefined/{}) : comportement inchangé pour
  // Messenger.js, qui ne les passe pas. `status` filtre côté serveur (listConversation), utilisé
  // par ex. par l'Inbox admin. `options` (skip/context) sert aussi à l'Inbox.
  const { skip = false, context } = options;
  const { hasNewMessages, clearMessages } = useNotifications();
  const [conversations, setConversations] = useState([]);
  const [paginationParams, setPaginationParams] = useState(
    initialPaginationParams
  );

  const { data, loading, error, refetch } = useQuery(QR_LIST_CONVERSATION, {
    variables: {
      paginationParams: initialPaginationParams,
      establishmentId,
      customerId,
      assignedAgentId,
      status,
    },
    fetchPolicy: "cache-and-network",
    skip,
    context,
  });

  const businessError = data?.QR_LIST_CONVERSATION?.error ?? null;
  const conversationsTotalPages =
    data?.QR_LIST_CONVERSATION?.data?.totalPages ?? 0;
  const conversationsPage =
    data?.QR_LIST_CONVERSATION?.data?.currentPage ?? 0;

  // Indicateur pour savoir s'il y a d'autres pages à charger
  const hasMoreConversations =
    paginationParams.currentPage + 1 < conversationsTotalPages;

  useEffect(() => {
    if (data?.QR_LIST_CONVERSATION?.data?.content) {
      const newConvs = data.QR_LIST_CONVERSATION.data.content;

      if (paginationParams.currentPage === 0) {
        setConversations(newConvs);
      } else {
        setConversations((prev) => {
          const ids = new Set(prev.map((c) => c.id));
          const filtered = newConvs.filter((c) => !ids.has(c.id));
          return [...prev, ...filtered];
        });
      }
    }
  }, [data, paginationParams.currentPage]);

  useEffect(() => {
    if (hasNewMessages) {
      setPaginationParams(initialPaginationParams);
      refetch({
        paginationParams: initialPaginationParams,
        establishmentId,
        customerId,
        assignedAgentId,
        status,
      });
      clearMessages();
    }
  }, [
    hasNewMessages,
    refetch,
    initialPaginationParams,
    establishmentId,
    customerId,
    assignedAgentId,
    status,
    clearMessages,
  ]);

  const loadMoreConversations = async () => {
    if (hasMoreConversations) {
      const nextPageParams = {
        ...paginationParams,
        currentPage: paginationParams.currentPage + 1,
      };
      setPaginationParams(nextPageParams);
      await refetch({
        paginationParams: nextPageParams,
        establishmentId,
        customerId,
        assignedAgentId,
        status,
      });
    }
  };

  const resetConversations = async () => {
    setPaginationParams(initialPaginationParams);
    await refetch({
      paginationParams: initialPaginationParams,
      establishmentId,
      customerId,
      assignedAgentId,
      status,
    });
  };

  return {
    conversations,
    loading,
    error: error || businessError,
    refetch: resetConversations,
    loadMoreConversations,
    conversationsPage,
    conversationsTotalPages,
    hasMoreConversations, // 🔹 exposé pour le composant
  };
};
